import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ShieldAlert, TrendingDown, XOctagon } from "lucide-react";

/**
 * ANATRADEX — Risk Disclosure Statement
 * The single most important legal document for a trading platform.
 * Protects the company from lawsuits by users who lose money.
 */

export default function RiskDisclosure() {
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="risk-disclosure-page">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="risk-back-link">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Risk Disclosure</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={14} /> Retour
          </Link>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-warn/10 border border-warn/40 rounded-full text-xs uppercase tracking-widest text-warn mb-4">
          <AlertTriangle size={12} /> Avertissement obligatoire · À lire avant utilisation
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-6">
          Déclaration de risques du trading
        </h1>
        <p className="text-white/60 text-sm mb-10">Dernière mise à jour : février 2026 · Version 1.0</p>

        <div className="rounded-2xl border-2 border-warn/50 bg-warn/10 p-6 mb-10" data-testid="risk-critical-notice">
          <div className="flex items-start gap-3">
            <XOctagon className="text-warn shrink-0 mt-0.5" size={28} />
            <div>
              <div className="font-heading font-bold text-warn text-xl mb-2">Le trading n&apos;est pas un jeu.</div>
              <p className="text-white/90 leading-relaxed">
                Le trading sur les marchés financiers (Forex, indices, matières premières, crypto-actifs, actions, ETF)
                comporte un <b>risque de perte substantiel, pouvant aller jusqu&apos;à la totalité du capital investi</b>,
                voire au-delà en cas d&apos;utilisation d&apos;effet de levier. En engageant votre argent réel,
                vous acceptez ce risque en toute connaissance de cause.
              </p>
            </div>
          </div>
        </div>

        <div className="prose-content space-y-8 text-white/80 leading-relaxed">

          <section>
            <h2 className="font-heading text-2xl mb-4 flex items-center gap-2">
              <TrendingDown className="text-down" size={22} /> 1. Nature des risques encourus
            </h2>
            <p>Trader implique les risques suivants, dont la liste n&apos;est pas exhaustive :</p>
            <ul className="space-y-2 mt-3">
              <li><b className="text-white">Risque de marché</b> — les prix évoluent en fonction de facteurs macro-économiques, géopolitiques et techniques que vous ne contrôlez pas. Une position peut perdre 100 % de sa valeur en quelques minutes.</li>
              <li><b className="text-white">Risque d&apos;effet de levier</b> — l&apos;utilisation de la marge amplifie les gains ET les pertes. Une variation de prix de 1 % avec un levier de 100:1 peut effacer votre capital entier.</li>
              <li><b className="text-white">Risque de liquidité</b> — dans des conditions de marché stressées, vous pouvez ne pas pouvoir clôturer une position au prix souhaité, ou pas du tout.</li>
              <li><b className="text-white">Risque de gap</b> — les marchés peuvent ouvrir avec un écart brutal, rendant les stop-loss inefficaces.</li>
              <li><b className="text-white">Risque de contrepartie</b> — votre broker peut faire faillite. Vérifiez qu&apos;il est régulé et couvert par un fonds de garantie.</li>
              <li><b className="text-white">Risque technologique</b> — pannes internet, latence, bugs, cyber-attaques peuvent empêcher l&apos;exécution ou la clôture d&apos;ordres.</li>
              <li><b className="text-white">Risque psychologique</b> — l&apos;overtrading, l&apos;émotion, l&apos;addiction sont statistiquement responsables de la majorité des pertes retail. Le trading peut être psychologiquement destructeur.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4 flex items-center gap-2">
              <ShieldAlert className="text-warn" size={22} /> 2. Statistiques que vous devez connaître
            </h2>
            <p>Selon les données publiées par les régulateurs européens (ESMA, AMF) et américains (SEC, FINRA) :</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="rounded-lg border border-down/30 bg-down/5 p-4">
                <div className="text-down text-3xl font-bold font-mono">70-85%</div>
                <div className="text-sm text-white/70 mt-1">des traders retail sur CFDs perdent de l&apos;argent (moyenne européenne, obligation de disclosure ESMA)</div>
              </div>
              <div className="rounded-lg border border-down/30 bg-down/5 p-4">
                <div className="text-down text-3xl font-bold font-mono">{"<"} 10%</div>
                <div className="text-sm text-white/70 mt-1">de taux de réussite moyen sur les challenges prop firm (FTMO, Topstep, MyForexFunds)</div>
              </div>
              <div className="rounded-lg border border-down/30 bg-down/5 p-4">
                <div className="text-down text-3xl font-bold font-mono">80%</div>
                <div className="text-sm text-white/70 mt-1">des day-traders abandonnent dans les 2 ans (source : Barber & Odean, University of California)</div>
              </div>
              <div className="rounded-lg border border-down/30 bg-down/5 p-4">
                <div className="text-down text-3xl font-bold font-mono">1-3%</div>
                <div className="text-sm text-white/70 mt-1">seulement des traders retail sont profitables sur 5 ans (source : études broker-side)</div>
              </div>
            </div>
            <p className="mt-4"><b className="text-warn">Ces chiffres vous concernent</b>. Statistiquement, si vous débutez avec du capital réel sans expérience, vous êtes plus proche des 85 % perdants que des 3 % gagnants.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">3. Nature exacte du service ANATRADEX</h2>
            <p><b className="text-white">ANATRADEX est un outil d&apos;analyse et d&apos;apprentissage.</b> Nous fournissons :</p>
            <ul className="space-y-2 mt-3">
              <li>Des analyses techniques automatisées (signaux ZII/DP/RL, régime, order flow)</li>
              <li>Des outils de backtest et de calcul statistique (Sharpe, PBO, DSR, walk-forward)</li>
              <li>Du contenu pédagogique (Academy)</li>
              <li>Un suivi de portefeuille et de risque</li>
            </ul>
            <p className="mt-4"><b className="text-warn">Nous NE fournissons PAS :</b></p>
            <ul className="space-y-2 mt-3">
              <li>De conseil financier personnalisé au sens de la MiFID II</li>
              <li>De service de gestion de portefeuille</li>
              <li>De garantie de rendement, de succès ou de préservation du capital</li>
              <li>D&apos;exécution d&apos;ordres pour votre compte (les ordres sont passés chez votre broker, sous votre entière responsabilité)</li>
              <li>De recommandation d&apos;achat/vente au sens réglementaire</li>
            </ul>
            <p className="mt-4">
              Les signaux affichés dans l&apos;application sont des <b>indications techniques calculées algorithmiquement</b>.
              Ils ne constituent en aucun cas un conseil en investissement, une incitation à investir, ni une prédiction du marché.
              <b className="text-warn"> Toute décision de trade que vous prenez est intégralement la vôtre.</b>
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">4. Backtest et données de démonstration</h2>
            <div className="rounded-lg border border-warn/40 bg-warn/5 p-4">
              <p><b className="text-warn">Avertissement statistique important :</b></p>
              <p className="mt-2">Les backtests et métriques affichées (Sharpe, Profit Factor, PBO, DSR, drawdown, taux de succès)
              sont calculés sur des <b>données historiques ou de démonstration seedées algorithmiquement</b>. Elles :</p>
              <ul className="space-y-1 mt-2">
                <li>Ne garantissent en aucune manière la performance future</li>
                <li>Ne tiennent pas compte de vos coûts réels (spread, commission, slippage, financement overnight)</li>
                <li>Sont soumises à des biais possibles (survivorship bias, look-ahead bias, data snooping)</li>
                <li>Peuvent ne pas être reproductibles sur votre broker/compte spécifique</li>
              </ul>
              <p className="mt-2"><b>Passé ne préjuge pas du futur.</b> C&apos;est un principe universel en finance quantitative.</p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">5. Vos obligations</h2>
            <p>En utilisant ANATRADEX, vous vous engagez à :</p>
            <ul className="space-y-2 mt-3">
              <li><b className="text-white">Ne trader que du capital que vous pouvez vous permettre de perdre intégralement</b>, sans impact sur votre niveau de vie, votre logement, votre famille ou vos autres obligations financières</li>
              <li>Vous former en continu (voir notre <Link to="/academy" className="text-accent-blue underline">Academy</Link>) avant d&apos;engager du capital réel</li>
              <li>Utiliser un compte demo pendant au minimum 3 mois avant tout compte réel</li>
              <li>Définir et respecter votre stop-loss sur chaque trade</li>
              <li>Ne jamais risquer plus de 1-2 % de votre capital par trade</li>
              <li>Consulter un conseiller financier réglementé pour tout investissement significatif</li>
              <li>Déclarer et payer les taxes applicables sur vos gains selon votre juridiction fiscale</li>
              <li>Ne pas utiliser ANATRADEX si vous êtes mineur, sous tutelle, en situation de dépendance au jeu, ou dans un état psychologique altéré</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">6. Aide en cas de trouble</h2>
            <p>Si vous ressentez une perte de contrôle sur votre activité de trading (obsession, insomnie, pertes croissantes,
              trading pour « se refaire », emprunts pour trader, mensonges à l&apos;entourage), <b className="text-warn">arrêtez immédiatement</b> et contactez :</p>
            <ul className="space-y-2 mt-3">
              <li><b className="text-white">France</b> : Joueurs Info Service — 09 74 75 13 13 (appel non surtaxé)</li>
              <li><b className="text-white">Belgique</b> : Aide aux joueurs — 0800 35 777</li>
              <li><b className="text-white">Suisse</b> : Sos-jeu — 0800 040 080</li>
              <li><b className="text-white">Canada</b> : Aide Jeu (Québec) — 1 800 461-0140</li>
              <li><b className="text-white">International</b> : Gamblers Anonymous — <a href="https://www.gamblersanonymous.org" target="_blank" rel="noreferrer" className="text-accent-blue underline">gamblersanonymous.org</a></li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">7. Exclusion de responsabilité</h2>
            <p>ANATRADEX, ses fondateurs, employés, prestataires, actionnaires et affiliés <b>ne peuvent être tenus responsables</b>, dans les limites autorisées par la loi applicable, de :</p>
            <ul className="space-y-2 mt-3">
              <li>Toute perte financière, directe ou indirecte, résultant de l&apos;utilisation de la plateforme</li>
              <li>Toute décision de trading prise sur la base des analyses fournies</li>
              <li>Toute erreur, retard, indisponibilité, bug, ou anomalie de la plateforme</li>
              <li>Tout dommage moral, psychologique, patrimonial ou familial découlant de votre activité de trading</li>
              <li>Toute mauvaise interprétation des données affichées</li>
              <li>Tout comportement de tiers (brokers, prop firms, exchanges) que vous engagez sur la base de nos analyses</li>
            </ul>
            <p className="mt-4">La responsabilité maximale d&apos;ANATRADEX en cas de faute prouvée est <b>strictement limitée au montant des 12 derniers mois d&apos;abonnement</b> effectivement payés.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">8. Juridictions restreintes</h2>
            <p>L&apos;utilisation d&apos;ANATRADEX peut être interdite ou réglementée dans certains pays. Il est de votre responsabilité de vérifier la conformité avec les lois locales. ANATRADEX se réserve le droit de refuser l&apos;accès à toute personne résidant dans une juridiction où le service serait illégal ou soumis à autorisation préalable non obtenue.</p>
            <p className="mt-3">Le service n&apos;est <b>pas destiné aux résidents des États-Unis</b> sans conformité avec la réglementation FINRA/SEC applicable, ni aux ressortissants de pays sous embargo international.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-4">9. Acceptation</h2>
            <p>En créant un compte, en payant un abonnement, ou en utilisant tout ou partie de la plateforme ANATRADEX,
              vous <b>reconnaissez avoir lu et compris intégralement la présente Déclaration de risques</b> ainsi que
              nos <Link to="/terms" className="text-accent-blue underline">Conditions Générales</Link> et
              notre <Link to="/privacy" className="text-accent-blue underline">Politique de Confidentialité</Link>, et
              vous acceptez d&apos;en respecter tous les termes.</p>
            <p className="mt-3">Ce document doit être conservé et consulté régulièrement. Il peut être modifié à tout moment,
              et l&apos;utilisation continue de la plateforme après modification vaut acceptation de la nouvelle version.</p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <div className="text-xs text-white/40 mb-4">
            ANATRADEX · Contact : legal@anatradex.com · Dernière révision : février 2026
          </div>
          <div className="flex justify-center gap-4 text-xs">
            <Link to="/terms" className="text-white/60 hover:text-white">Conditions Générales</Link>
            <span className="text-white/20">·</span>
            <Link to="/privacy" className="text-white/60 hover:text-white">Confidentialité</Link>
            <span className="text-white/20">·</span>
            <Link to="/cookies" className="text-white/60 hover:text-white">Cookies</Link>
          </div>
        </div>
      </article>
    </div>
  );
}
